import { useState } from 'react';
import { Form } from 'react-bootstrap';

const inr = (n) => `₹${Number(n).toLocaleString('en-IN')}`;

// A dual-thumb price slider built from two native <input type="range"> on one track - no extra
// dependency. Shared, identical, across every vertical's ItemList.
//
//  value              { min, max } - numbers, or null / '' for "no bound"
//  onChange({min,max}) fires once per drag (on release), not per pixel - min comes back null at
//                     0, max null at the track's top
//  ceiling            fixed top of the track. Small rental businesses (jewelry / car / stays)
//                     pass 50000.
//  ceilingOptions     [{ label, value }] - when given, a <select> lets the shopper raise/lower
//                     the track's top (real-estate budgets span lakhs to crores). Changing it
//                     resets the range. `allowCustomCeiling` adds a "Custom maximum…" entry with
//                     a free number input.
//  defaultCeiling     which ceilingOptions value the slider starts on (default: the last one)
export default function PriceRangeFilter({
  value = {},
  onChange,
  ceiling = 50000,
  ceilingOptions,
  defaultCeiling,
  allowCustomCeiling = false
}) {
  const [pickedCeiling, setPickedCeiling] = useState(
    defaultCeiling ?? (ceilingOptions ? ceilingOptions[ceilingOptions.length - 1].value : ceiling)
  );
  const [customCeiling, setCustomCeiling] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  // While a thumb is being dragged we hold the position here and only push it to the parent
  // (URL param -> refetch) on release.
  const [draft, setDraft] = useState(null);

  const top = ceilingOptions
    ? (isCustom ? Math.max(1, Number(customCeiling) || 0) : pickedCeiling)
    : ceiling;
  const step = Math.max(1, Math.round(top / 100));

  const fromProps = (v, fallback) => (v == null || v === '' ? fallback : Math.min(Number(v), top));
  const lo = draft ? draft.lo : fromProps(value.min, 0);
  const hi = draft ? draft.hi : fromProps(value.max, top);

  const commit = () => {
    if (!draft) return;
    onChange({ min: draft.lo <= 0 ? null : draft.lo, max: draft.hi >= top ? null : draft.hi });
    setDraft(null);
  };

  const bounded = lo > 0 || hi < top;
  const pct = (n) => (top ? Math.min(100, Math.max(0, (n / top) * 100)) : 0);

  const changeCeiling = (raw) => {
    setDraft(null);
    if (raw === '__custom') {
      setIsCustom(true);
      onChange({ min: null, max: null });
      return;
    }
    setIsCustom(false);
    setPickedCeiling(Number(raw));
    onChange({ min: null, max: null }); // the scale changed - start again from "Any"
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-baseline mb-1">
        <span className="small text-muted">Price range</span>
        <span className="small">
          {bounded ? `${inr(lo)} – ${hi >= top ? `${inr(top)}+` : inr(hi)}` : 'Any'}
        </span>
      </div>

      <div className="price-range-slider mb-2">
        <div className="price-range-slider__rail" />
        <div
          className="price-range-slider__fill"
          style={{ left: `${pct(lo)}%`, right: `${100 - pct(hi)}%` }}
        />
        <input
          type="range" aria-label="Minimum price"
          min={0} max={top} step={step} value={lo}
          onChange={(e) => setDraft({ lo: Math.min(Number(e.target.value), hi), hi })}
          onMouseUp={commit} onTouchEnd={commit} onKeyUp={commit} onBlur={commit}
        />
        <input
          type="range" aria-label="Maximum price"
          min={0} max={top} step={step} value={hi}
          onChange={(e) => setDraft({ lo, hi: Math.max(Number(e.target.value), lo) })}
          onMouseUp={commit} onTouchEnd={commit} onKeyUp={commit} onBlur={commit}
        />
      </div>

      {ceilingOptions && (
        <>
          <Form.Select
            size="sm" aria-label="Maximum budget"
            value={isCustom ? '__custom' : String(pickedCeiling)}
            onChange={(e) => changeCeiling(e.target.value)}
          >
            {ceilingOptions.map((o) => (
              <option key={o.value} value={o.value}>Up to {o.label}</option>
            ))}
            {allowCustomCeiling && <option value="__custom">Custom maximum…</option>}
          </Form.Select>
          {isCustom && (
            <Form.Control
              size="sm" type="number" min={1} className="mt-1"
              placeholder="Enter a maximum ₹"
              value={customCeiling}
              onChange={(e) => setCustomCeiling(e.target.value)}
            />
          )}
        </>
      )}
    </div>
  );
}
